import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Infinity as InfinityIcon, TrendingDown, Zap, PlusCircle } from "lucide-react";
import { api } from "../lib/api";

const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString());

export default function Usage() {
  const [data, setData] = useState(null);

  const PAGE_SIZE = 10;

  const [txnPage, setTxnPage] = useState(1);
  const [txns, setTxns] = useState([]);

  const totalTxnPages = Math.max(1, Math.ceil(txns.length / PAGE_SIZE));

  const paginatedTxns = txns.slice(
    (txnPage - 1) * PAGE_SIZE,
    txnPage * PAGE_SIZE
  );


  useEffect(() => {
    const load = async () => {
      const r = await api.get("/credits/usage");
      setData(r.data);
      const t = await api.get("/credits/project-usage", { params: { limit: 30 } });
      // const t = await api.get("/credits/transactions", { params: { limit: 30 } });
      setTxns(t.data.items || []);
    };
    load();
  }, []);

  if (!data) {
    return <div className="p-10 text-[#64748B]" data-testid="usage-loading">Loading usage…</div>;
  }

  const frac = data.is_unlimited ? 1.0 : data.balance_fraction;
  const barColor = (() => {
    if (data.is_unlimited) return "#6366F1";
    if (data.balance <= 0 || frac <= 0.10) return "#EF4444";
    if (frac <= 0.25) return "#F59E0B";
    return "#10B981";
  })();



  return (
    <div className="p-8 lg:p-12" data-testid="usage-page">
      <div className="type-eyebrow text-[#64748B]">Credits</div>
      <h1 className="type-h1 text-[#0F1F3D] mt-3">Usage and balance</h1>
      <p className="type-body text-[#64748B] mt-3">
        Scraping is free. One credit per AI relevance score. Consumed as your projects run.
      </p>

      {/* Balance card */}
      <div className="mt-10 bg-white border border-[#E2E8F0] rounded-lg p-8" data-testid="usage-balance-card">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="type-eyebrow text-[#64748B]">Current plan</div>
            <div className="type-h2 text-[#0F1F3D] mt-2">{data.plan.label}</div>
            <div className="type-caption text-[#64748B] mt-1">{data.plan.description}</div>
          </div>
          <div className="text-right">
            <div className="type-eyebrow text-[#64748B]">Balance</div>
            <div className="type-h2 text-[#0F1F3D] font-mono-tab flex items-center gap-2 justify-end mt-2" data-testid="usage-balance-value">
              {data.is_unlimited ? (
                <>
                  <InfinityIcon className="h-6 w-6 text-[#6366F1]" />
                  <span className="text-[#6366F1]">Unlimited</span>
                </>
              ) : (
                fmt(data.balance)
              )}
            </div>
            {!data.is_unlimited && (
              <div className="type-caption text-[#64748B] mt-2">of {fmt(data.starting_balance)} monthly credits</div>
            )}
            {!data.is_unlimited && (
              <button
                type="button"
                onClick={() => { }}
                disabled
                data-testid="usage-topup-button"
                title="Top-ups coming soon. Contact your ORL admin to upgrade plans."
                className="mt-4 inline-flex items-center gap-2 px-3.5 py-2 rounded-md border border-[#0F1F3D]/20 bg-[#F8F9FA] text-[#475569] type-caption font-semibold cursor-not-allowed"
              >
                <PlusCircle className="h-3.5 w-3.5" />
                Top up credits
                <span className="ml-1.5 px-1.5 py-0.5 rounded-sm bg-[#0F1F3D] text-white text-[9px] uppercase tracking-wider">Coming soon</span>
              </button>
            )}
          </div>
        </div>
        {/* Progress bar */}
        <div className="mt-8">
          <div className="h-3 w-full bg-[#F1F5F9] rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${Math.min(100, Math.max(0, frac * 100))}%`, backgroundColor: barColor }}
              data-testid="usage-balance-bar"
            />
          </div>
        </div>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-[#F8F9FA] rounded-md p-5">
            <div className="flex items-center gap-2 type-eyebrow text-[#64748B]">
              <TrendingDown className="h-3 w-3" /> Daily burn (7d avg)
            </div>
            <div className="font-mono-tab text-2xl font-semibold text-[#0F1F3D] mt-3" data-testid="usage-daily-burn">
              {fmt(data.daily_burn)}
            </div>
          </div>
          <div className="bg-[#F8F9FA] rounded-md p-5">
            <div className="flex items-center gap-2 type-eyebrow text-[#64748B]">
              <Zap className="h-3 w-3" /> Days remaining
            </div>
            <div className="font-mono-tab text-2xl font-semibold text-[#0F1F3D] mt-3" data-testid="usage-est-days">
              {data.is_unlimited ? "∞" : data.estimated_days_remaining == null ? "—" : data.estimated_days_remaining}
            </div>
          </div>
          <div className="bg-[#F8F9FA] rounded-md p-5">
            <div className="type-eyebrow text-[#64748B]">Period started</div>
            <div className="type-body text-[#0F1F3D] mt-3" data-testid="usage-period-start">
              {data.period_start ? new Date(data.period_start).toLocaleDateString() : "—"}
            </div>
          </div>
        </div>
      </div>

      {/* Usage by project */}
      <div className="mt-10 bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="usage-by-project">
        <div className="px-6 py-5 border-b border-[#E2E8F0]">
          <div className="type-eyebrow text-[#64748B]">Projects</div>
          <div className="type-h3 text-[#0F1F3D] mt-1">Usage by project</div>
          <div className="type-caption text-[#64748B] mt-1">Credits consumed per project across the full account history.</div>
        </div>
        <table className="w-full">
          <thead className="bg-[#F8F9FA] type-eyebrow text-[#64748B]">
            <tr>
              <th className="text-left px-6 py-3">Project</th>
              <th className="text-right px-6 py-3">Credits spent</th>
              <th className="text-right px-6 py-3">Runs</th>
            </tr>
          </thead>
          <tbody>
            {(data.by_project || []).length === 0 && (
              <tr><td colSpan="3" className="px-6 py-12 text-center text-[#94A3B8] type-body">No credits consumed yet. Run a pipeline to see usage here.</td></tr>
            )}
            {(data.by_project || []).map((row) => (
              <tr key={row.project_id || "none"} className="border-t border-[#E2E8F0]" data-testid={`usage-project-row-${row.project_id || "none"}`}>
                <td className="px-6 py-4">
                  {row.project_id ? (
                    <Link to={`/app/projects/${row.project_id}`} className="type-h4 text-[#0F1F3D] hover:underline">{row.project_name}</Link>
                  ) : (
                    <span className="type-body text-[#94A3B8]">(unattributed)</span>
                  )}
                </td>
                <td className="px-6 py-4 text-right font-mono-tab text-sm text-[#0F1F3D] font-bold">{fmt(row.spent)}</td>
                <td className="px-6 py-4 text-right font-mono-tab type-caption text-[#64748B]">{fmt(row.transactions)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Transaction history */}
      <div className="mt-10 bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="usage-transactions">
        <div className="px-6 py-5 border-b border-[#E2E8F0]">
          <div className="type-eyebrow text-[#64748B]">Audit</div>
          <div className="type-h3 text-[#0F1F3D] mt-1">Recent transactions</div>
          <div className="type-caption text-[#64748B] mt-1">Every credit charge is logged here for audit.</div>
        </div>
        <table className="w-full">
          <thead className="bg-[#F8F9FA] type-eyebrow text-[#64748B]">
            <tr>
              <th className="text-left px-6 py-3">Date</th>
              <th className="text-left px-6 py-3">Project</th>
              <th className="text-right px-6 py-3">Results fetched</th>
              <th className="text-right px-6 py-3">Credit Used</th>
            </tr>
          </thead>
          <tbody>
            {txns.length === 0 && (
              <tr><td colSpan="4" className="px-6 py-12 text-center text-[#94A3B8] type-body">No transactions yet.</td></tr>
            )}
            {paginatedTxns.map((t) => (
              <tr key={t.id} className="border-t border-[#E2E8F0]" data-testid={`usage-txn-${t.project_id}`}>
                <td className="px-6 py-3 type-caption text-[#64748B]">{new Date(t.date).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}</td>
                <td className="px-6 py-3 type-body-sm text-[#0F1F3D]">{t.project_name.replace(/_/g, " ")}</td>
                {/* <td className={`px-6 py-3 text-right font-mono-tab text-sm font-bold ${t.news_fetched >= 0 ? "text-[#B91C1C]" : "text-[#065F46]"}`}> */}
                <td className={`px-6 py-3 text-right font-mono-tab text-sm font-bold`}>
                  {t.news_fetched}
                </td>
                <td className="px-6 py-3 text-right font-mono-tab type-caption text-[#64748B]">{t.credits_used < 0 ? "∞" : fmt(t.credits_used)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {txns.length > PAGE_SIZE && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-[#E2E8F0] bg-white">
            <p className="type-caption text-[#64748B]">
              Showing {(txnPage - 1) * PAGE_SIZE + 1}–
              {Math.min(txnPage * PAGE_SIZE, txns.length)} of {txns.length} transactions
            </p>

            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={txnPage === 1}
                onClick={() => setTxnPage((p) => Math.max(1, p - 1))}
                className="px-4 py-2 rounded-md border border-[#E2E8F0] text-sm font-medium text-[#475569] hover:text-[#0F1F3D] hover:border-[#CBD5E1] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              <span className="px-3 text-sm text-[#64748B]">
                {txnPage} / {totalTxnPages}
              </span>

              <button
                type="button"
                disabled={txnPage === totalTxnPages}
                onClick={() =>
                  setTxnPage((p) => Math.min(totalTxnPages, p + 1))
                }
                className="px-4 py-2 rounded-md border border-[#E2E8F0] text-sm font-medium text-[#475569] hover:text-[#0F1F3D] hover:border-[#CBD5E1] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
